import React, { useEffect, useMemo, useState } from "react";
import { FaPlus, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import WebService from "../Services/WebService";
import styles from "./OrderChargesEditor.module.css";

export interface OrderCharge {
  sale_order_charge_id?: number;
  description: string;
  amount: number | string;
}

interface Props {
  /** Omit for a new order that isn't created yet: rows are kept as a draft via onDraftChange. */
  saleOrderId?: number | null;
  /** Saved charges from the order (`other_charges`). */
  charges?: OrderCharge[] | null;
  editable: boolean;
  /** Called after a successful save so the parent can reload totals. */
  onSaved: () => void;
  /** Draft mode (no saleOrderId): receives the filled rows on every change. */
  onDraftChange?: (charges: OrderCharge[]) => void;
}

type Row = { description: string; amount: string };

const toRows = (charges?: OrderCharge[] | null): Row[] =>
  (charges || []).map((c) => ({ description: c.description || "", amount: String(c.amount ?? "") }));

const inr = (n: number) => "₹" + Math.abs(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });

/**
 * Order-level "other charges": one line per charge (description + signed amount).
 * Positive adds to the total, negative reduces it. Sits after "Total Packaging".
 */
const OrderChargesEditor: React.FC<Props> = ({ saleOrderId, charges, editable, onSaved, onDraftChange }) => {
  const saved = useMemo(() => toRows(charges), [charges]);
  const [rows, setRows] = useState<Row[]>(saved);
  const [saving, setSaving] = useState(false);

  const isDraft = !saleOrderId;
  // Saved mode follows the server list; a draft owns its rows (half-typed lines must not vanish).
  useEffect(() => {
    if (!isDraft) setRows(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved]);
  useEffect(() => {
    if (!isDraft || !onDraftChange) return;
    onDraftChange(
      rows
        .filter((r) => r.description.trim() && Number(r.amount))
        .map((r) => ({ description: r.description.trim(), amount: Number(r.amount) }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, isDraft]);

  if (!editable) {
    return (
      <>
        {saved.map((r, i) => {
          const n = Number(r.amount) || 0;
          return (
            <div key={i} className={styles.readRow}>
              <span>{r.description}</span>
              <span>{n < 0 ? "−" : ""}{inr(n)}</span>
            </div>
          );
        })}
      </>
    );
  }

  const dirty = JSON.stringify(rows.filter((r) => r.description || r.amount)) !== JSON.stringify(saved);
  const update = (i: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const save = async () => {
    const filled = rows.filter((r) => r.description.trim() || r.amount.trim());
    for (const r of filled) {
      if (!r.description.trim()) return toast.error("Add a description for each charge.");
      const n = Number(r.amount);
      if (!r.amount.trim() || !Number.isFinite(n) || n === 0) {
        return toast.error(`Enter an amount for "${r.description.trim()}".`);
      }
    }
    setSaving(true);
    try {
      await WebService.putAPI({
        action: `/v1/door-management/saleorders/${saleOrderId}/other-charges`,
        body: { charges: filled.map((r) => ({ description: r.description.trim(), amount: Number(r.amount) })) },
      });
      toast.success("Charges saved. Total updated.");
      onSaved();
    } catch {
      // WebService shows the error
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.titleRow}>
        <span>Other Charges</span>
        <button
          type="button"
          className={styles.addBtn}
          onClick={() => setRows((prev) => [...prev, { description: "", amount: "" }])}
          title="Add a charge"
        >
          <FaPlus size={11} /> Add
        </button>
      </div>

      {rows.map((r, i) => (
        <div key={i} className={styles.row}>
          <input
            className={styles.desc}
            placeholder="Description (e.g. Polish, Hardware)"
            value={r.description}
            maxLength={120}
            onChange={(e) => update(i, { description: e.target.value })}
          />
          <input
            className={styles.amount}
            inputMode="decimal"
            placeholder="± Amount"
            title="Positive adds to the total, negative reduces it"
            value={r.amount}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || v === "-" || /^-?\d*\.?\d{0,2}$/.test(v)) update(i, { amount: v });
            }}
          />
          <button
            type="button"
            className={styles.removeBtn}
            onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
            title="Remove"
          >
            <FaTimes size={12} />
          </button>
        </div>
      ))}

      {isDraft && rows.length > 0 && (
        <div className={styles.draftHint}>Saved with the order when you press Confirm.</div>
      )}
      {!isDraft && dirty && (
        <button type="button" className={styles.saveBtn} disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save charges"}
        </button>
      )}
    </div>
  );
};

export default OrderChargesEditor;
